import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, createSelector } from '@ngxs/store';
import { catchError, tap } from 'rxjs/operators';
import { EMPTY, Observable } from 'rxjs';

import { SelectProject } from '../auth/store/auth.actions';
import {
  CapabilitiesUnavailable,
  LoadCapabilities,
  RefreshCapabilities
} from './capabilities.actions';
import { CapabilitiesError, EffectiveCapabilities } from './capabilities.model';
import { CapabilitiesService } from './capabilities.service';

/** Roles de organización que el backend deja administrar perfiles: MANAGER o superior. */
const ROLES_QUE_ADMINISTRAN_PERFILES = ['MANAGER', 'ORG_ADMIN', 'SUPERADMIN'];

export class CapabilitiesStateModel {
  effective: EffectiveCapabilities | null = null;
  /** Proyecto al que corresponde el conjunto cargado. */
  projectId: string | null = null;
  loading = false;
  error: CapabilitiesError = null;
}

/**
 * El conjunto efectivo de capacidades del usuario en el proyecto activo.
 *
 * Dos cosas gobiernan este estado y conviene decirlas porque son las que rompen las
 * implementaciones ingenuas:
 *
 * 1. **El conjunto pertenece a un proyecto, no al usuario.** Por eso se guarda junto al proyecto al
 *    que corresponde, y por eso el selector de carga compara ambos: mientras no coincidan, lo
 *    cargado es del proyecto anterior y no vale. Pintar con él enseñaría al usuario los permisos
 *    que tenía en otro sitio.
 * 2. **Ocultar no es autorizar.** Nada de lo que hay aquí protege nada; solo evita ofrecer botones
 *    que el backend va a rechazar. Quien llame al endpoint a mano recibe el mismo 403.
 */
@State<CapabilitiesStateModel>({
  name: 'capabilities',
  defaults: new CapabilitiesStateModel()
})
@Injectable()
export class CapabilitiesState {
  constructor(private readonly service: CapabilitiesService) {}

  @Selector()
  static effective(state: CapabilitiesStateModel): EffectiveCapabilities | null {
    return state.effective;
  }

  @Selector()
  static loading(state: CapabilitiesStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static error(state: CapabilitiesStateModel): CapabilitiesError {
    return state.error;
  }

  /** Clave del perfil efectivo. La etiqueta la resuelve la traducción, aquí nunca hay literales. */
  @Selector()
  static profile(state: CapabilitiesStateModel): string | null {
    return state.effective?.profile ?? null;
  }

  @Selector()
  static orgRole(state: CapabilitiesStateModel): string | null {
    return state.effective?.orgRole ?? null;
  }

  /**
   * ¿Puede administrar los perfiles de ciclo de vida del proyecto (P-10)?
   *
   * Se decide por el rol y no por una capacidad porque así lo decide el backend: los endpoints de
   * P-10 exigen MANAGER o superior (`ProjectRoleGuard.requireManagerOrAbove`) y no consultan el
   * catálogo. Administrar perfiles no es una capacidad de producto, y exigir aquí una que el catálogo
   * no publica dejaba la pantalla cerrada para todos, incluidos quienes el backend sí deja pasar.
   */
  @Selector()
  static canAdminProfiles(state: CapabilitiesStateModel): boolean {
    return ROLES_QUE_ADMINISTRAN_PERFILES.includes(state?.effective?.orgRole ?? '');
  }

  @Selector()
  static granted(state: CapabilitiesStateModel): string[] {
    return state.effective?.granted ?? [];
  }

  /**
   * ¿Está el conjunto listo para el proyecto que se le pasa?
   *
   * Es lo que mira el guard antes de dejar pintar. Devuelve falso mientras lo cargado sea de otro
   * proyecto, que es justamente el caso que el criterio prohíbe pintar.
   */
  static isReadyFor(projectId: string | null) {
    // `createSelector` y no una funcion suelta: una funcion suelta recibe el estado raiz de NGXS,
    // no la porcion de este estado, y devolveria siempre falso sin que nada lo delate.
    return createSelector([CapabilitiesState], (state: CapabilitiesStateModel): boolean =>
      !!projectId && state.projectId === projectId && !!state.effective && !state.loading);
  }

  /** ¿Concede el conjunto esta capacidad? Sin conjunto cargado, no. */
  static has(capability: string) {
    return createSelector([CapabilitiesState], (state: CapabilitiesStateModel): boolean =>
      (state.effective?.granted ?? []).includes(capability));
  }

  @Action(LoadCapabilities)
  load(ctx: StateContext<CapabilitiesStateModel>, action: LoadCapabilities): Observable<unknown> {
    ctx.patchState({ loading: true, error: null, projectId: action.projectId });
    return this.service.fetch().pipe(
      tap(effective => ctx.patchState({ effective, loading: false, error: null })),
      catchError(() => {
        // No se conserva el conjunto anterior: pertenecía a otra combinación de usuario y proyecto,
        // y servirlo aquí seria enseñar permisos que quizá ya no existen.
        ctx.patchState({ effective: null, loading: false, error: 'unavailable' });
        return EMPTY;
      })
    );
  }

  /**
   * El cambio de proyecto recarga el conjunto.
   *
   * Se escucha la acción del estado de autenticación en vez de que aquel llame a este: así el
   * cambio de proyecto no tiene que acordarse de nada, y no puede olvidarse desde una pantalla
   * nueva que cambie de proyecto por su cuenta.
   */
  @Action(SelectProject)
  onProjectChange(
    ctx: StateContext<CapabilitiesStateModel>,
    action: SelectProject
  ): Observable<unknown> | void {
    const projectId = action.selectedProject?.id;
    if (!projectId) {
      ctx.setState(new CapabilitiesStateModel());
      return;
    }
    // Se limpia antes de pedir: entre el cambio y la respuesta no debe quedar nada del anterior.
    ctx.patchState({ effective: null, error: null });
    return ctx.dispatch(new LoadCapabilities(projectId));
  }

  @Action(RefreshCapabilities)
  refresh(ctx: StateContext<CapabilitiesStateModel>): Observable<unknown> | void {
    const { projectId } = ctx.getState();
    if (!projectId) {
      return;
    }
    return ctx.dispatch(new LoadCapabilities(projectId));
  }

  @Action(CapabilitiesUnavailable)
  unavailable(ctx: StateContext<CapabilitiesStateModel>): void {
    ctx.patchState({ error: 'unavailable', loading: false });
  }
}
